import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  MoreVertical, 
  ExternalLink, 
  BarChart2, 
  Edit3, 
  Trash2, 
  Globe, 
  Clock, 
  AlertCircle,
  Loader2,
  Layers,
  CheckCircle2,
  XCircle,
  ArrowUpDown,
  ChevronDown,
  BookOpen,
  Eye,
  HelpCircle,
  TrendingUp,
  Sparkles
} from 'lucide-react';
import { auth, db } from '../firebase';
import { collection, query, where, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { PageEditor } from './PageEditor';
import { useToast } from './Toast';
import { ConfirmationModal } from './ConfirmationModal';
import { AIAnalysisModal } from './AIAnalysisModal';

interface LandingPage {
  id: string;
  name: string;
  url: string;
  status: 'published' | 'draft' | 'archived';
  traffic: number;
  conversions: number;
  createdAt: any;
}

import { markChecklistItemCompleted } from '../src/lib/checklist';

export const LandingPagesView: React.FC<{ userData: any; onEditStateChange?: (isEditing: boolean) => void }> = ({ userData, onEditStateChange }) => {
  const { showToast } = useToast();
  const [pages, setPages] = useState<LandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'createdAt' | 'traffic' | 'conversions'>('createdAt');
  const [isCreating, setIsCreating] = useState(false);
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState<LandingPage | null>(null);
  const [newPageName, setNewPageName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('vendas');
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; pageId: string | null }>({
    isOpen: false,
    pageId: null
  });

  const onCreate = () => setIsCreating(true);

  useEffect(() => {
    if (onEditStateChange) {
      onEditStateChange(!!editingPageId);
    }
  }, [editingPageId, onEditStateChange]);

  useEffect(() => {
    if (!auth.currentUser) return;

    const q = query(
      collection(db, 'landing_pages'),
      where('userId', '==', auth.currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      try {
        const pagesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as LandingPage[];
        
        setPages(pagesData);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error("Error processing pages data:", err);
        setError("Erro ao processar dados das páginas.");
        setLoading(false);
      }
    }, (error) => {
      console.error("Error fetching pages:", error);
      setError("Não foi possível carregar suas páginas. Verifique sua conexão e permissões.");
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleCreatePage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPageName.trim() || !auth.currentUser) return;

    setIsCreating(true);
    const path = 'landing_pages';
    
    // Template data
    const templates: Record<string, any> = {
      vendas: {
        headline: 'Seu Título Matador Aqui',
        subheadline: 'Um subtítulo (subheadline) que reforça sua promessa principal e quebra objeções.',
        cta: 'Quero Começar Agora',
        ctaUrl: '#',
        benefits: ['Benefício 1: Resultado rápido', 'Benefício 2: Facilidade de uso', 'Benefício 3: Suporte premium'],
        theme: 'dark'
      },
      leads: {
        headline: 'Baixe Nosso Guia Gratuito',
        subheadline: 'Descubra como dobrar seus resultados em menos de 30 dias com nosso método.',
        cta: 'Baixar Guia Agora',
        ctaUrl: '#',
        benefits: ['Acesso instantâneo', 'Conteúdo exclusivo', '100% gratuito'],
        theme: 'light'
      },
      obrigado: {
        headline: 'Parabéns! Sua Inscrição foi Confirmada',
        subheadline: 'Verifique seu e-mail em instantes para acessar o conteúdo.',
        cta: 'Ir para Área de Membros',
        ctaUrl: '#',
        benefits: ['Acesso vitalício', 'Comunidade exclusiva', 'Bônus liberados'],
        theme: 'dark'
      }
    };

    const templateData = templates[selectedTemplate] || templates.vendas;

    try {
      const docRef = await addDoc(collection(db, path), {
        userId: auth.currentUser.uid,
        name: newPageName,
        template: selectedTemplate,
        status: 'draft',
        url: '', // Will be updated with ID
        traffic: 0,
        conversions: 0,
        createdAt: serverTimestamp(),
        ...templateData
      });
      
      // Update with real URL using ID
      await updateDoc(docRef, {
        url: `${window.location.origin}/p/${docRef.id}`
      });

      setNewPageName('');
      setIsCreating(false);
      setEditingPageId(docRef.id);
      showToast('Página criada com sucesso!', 'success');
      markChecklistItemCompleted('page');
    } catch (error: any) {
      console.error("Error creating page:", error);
      
      // Handle Firestore permission error with detailed info
      if (error.code === 'permission-denied') {
        const errInfo = {
          error: error.message,
          operationType: 'write',
          path: path,
          authInfo: {
            userId: auth.currentUser?.uid,
            email: auth.currentUser?.email,
            emailVerified: auth.currentUser?.emailVerified,
            isAnonymous: auth.currentUser?.isAnonymous,
            providerInfo: auth.currentUser?.providerData.map(provider => ({
              providerId: provider.providerId,
              displayName: provider.displayName,
              email: provider.email,
              photoUrl: provider.photoURL
            })) || []
          }
        };
        console.error('Firestore Error: ', JSON.stringify(errInfo));
        showToast(`Erro de permissão: ${error.message}`, 'error');
      } else {
        showToast("Erro ao criar página. Verifique sua conexão.", 'error');
      }
      
      setIsCreating(false);
    }
  };

  const handleDeletePage = async () => {
    if (!deleteModal.pageId) return;
    try {
      await deleteDoc(doc(db, 'landing_pages', deleteModal.pageId));
      showToast('Página excluída com sucesso.', 'success');
    } catch (error) {
      console.error("Error deleting page:", error);
      showToast('Erro ao excluir página.', 'error');
    } finally {
      setDeleteModal({ isOpen: false, pageId: null });
    }
  };

  const toggleStatus = async (page: LandingPage) => {
    const newStatus = page.status === 'published' ? 'draft' : 'published';
    try {
      await updateDoc(doc(db, 'landing_pages', page.id), {
        status: newStatus
      });
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const filteredPages = pages
    .filter(page => 
      page.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'traffic':
          return b.traffic - a.traffic;
        case 'conversions':
          return b.conversions - a.conversions;
        case 'createdAt':
        default:
          const dateA = a.createdAt?.seconds || 0;
          const dateB = b.createdAt?.seconds || 0;
          return dateB - dateA;
      }
    });

  if (loading) {
    return (
      <div className="h-[60vh] flex flex-col items-center justify-center space-y-4">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        <p className="text-slate-500 text-sm animate-pulse">Sincronizando seus ativos digitais...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[60vh] flex flex-col items-center justify-center space-y-6 text-center px-4">
        <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center text-red-500">
          <AlertCircle className="w-8 h-8" />
        </div>
        <div className="space-y-2">
          <h3 className="text-xl font-bold text-white">Falha na Sincronização</h3>
          <p className="text-slate-400 max-w-xs mx-auto">{error}</p>
        </div>
        <button 
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-white text-black font-bold rounded-xl hover:bg-slate-200 transition-all"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  if (editingPageId) {
    return <PageEditor pageId={editingPageId} onBack={() => setEditingPageId(null)} userData={userData} />;
  }

  return (
    <div className="p-6 lg:p-10 space-y-10 animate-fade-in">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 pb-4">
        <div className="space-y-2">
          <div className="flex items-center gap-4">
            <h1 className="text-3xl lg:text-4xl font-bold text-white tracking-tight">Landing Pages</h1>
            <div className="group relative">
              <HelpCircle className="w-6 h-6 lg:w-5 lg:h-5 text-slate-500 cursor-help hover:text-indigo-400 transition-colors" />
              <div className="absolute top-full left-0 mt-3 w-80 p-4 bg-slate-900 border border-white/10 rounded-2xl text-[13px] text-slate-400 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 shadow-2xl font-normal leading-relaxed backdrop-blur-xl">
                Aqui você gerencia todas as suas páginas de vendas. Você pode criar novas, editar o conteúdo com IA e acompanhar o tráfego e conversões de cada uma.
              </div>
            </div>
          </div>
          <p className="text-sm lg:text-base text-slate-400">Gerencie suas páginas e acompanhe a performance em tempo real.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 lg:gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input 
              type="text" 
              placeholder="Buscar páginas..."
              className="bg-white/[0.03] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm text-white focus:border-indigo-500 focus:outline-none transition-all w-full md:w-64"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="relative group flex-1 sm:flex-none">
            <div className="flex items-center justify-between sm:justify-start gap-2 px-4 py-3 bg-white/[0.03] border border-white/10 rounded-xl text-sm text-slate-400 hover:text-white hover:border-white/20 transition-all cursor-pointer">
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4" />
                <span className="capitalize">{sortBy === 'createdAt' ? 'Data' : sortBy === 'traffic' ? 'Tráfego' : sortBy === 'conversions' ? 'Conversões' : 'Nome'}</span>
              </div>
              <ChevronDown className="w-3 h-3 opacity-50" />
            </div>
            <div className="absolute right-0 top-full mt-2 w-full sm:w-48 bg-slate-900 border border-white/10 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden">
              <button onClick={() => setSortBy('createdAt')} className={`w-full text-left px-4 py-4 sm:py-3 text-[13px] font-bold uppercase tracking-widest hover:bg-white/5 transition-colors ${sortBy === 'createdAt' ? 'text-indigo-400 bg-indigo-500/5' : 'text-slate-500'}`}>Data de Criação</button>
              <button onClick={() => setSortBy('name')} className={`w-full text-left px-4 py-4 sm:py-3 text-[13px] font-bold uppercase tracking-widest hover:bg-white/5 transition-colors ${sortBy === 'name' ? 'text-indigo-400 bg-indigo-500/5' : 'text-slate-500'}`}>Nome (A-Z)</button>
              <button onClick={() => setSortBy('traffic')} className={`w-full text-left px-4 py-4 sm:py-3 text-[13px] font-bold uppercase tracking-widest hover:bg-white/5 transition-colors ${sortBy === 'traffic' ? 'text-indigo-400 bg-indigo-500/5' : 'text-slate-500'}`}>Maior Tráfego</button>
              <button onClick={() => setSortBy('conversions')} className={`w-full text-left px-4 py-4 sm:py-3 text-[13px] font-bold uppercase tracking-widest hover:bg-white/5 transition-colors ${sortBy === 'conversions' ? 'text-indigo-400 bg-indigo-500/5' : 'text-slate-500'}`}>Mais Conversões</button>
            </div>
          </div>

          <button 
            onClick={onCreate}
            className="px-6 py-3 bg-indigo-500 text-white text-sm font-bold rounded-xl shadow-lg shadow-indigo-500/20 hover:bg-indigo-400 transition-all flex items-center justify-center gap-2"
          >
            <Plus className="w-5 h-5" /> Nova Página
          </button>
        </div>
      </div>

      {/* Purpose and Guidance Section */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="premium-card p-6 bg-indigo-500/5 border-indigo-500/10 space-y-4">
          <div className="w-10 h-10 bg-indigo-500/10 rounded-xl flex items-center justify-center text-indigo-400">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-white uppercase tracking-widest">Propósito</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Centralize e gerencie seus ativos de vendas de alta conversão. Esta área permite que você visualize a saúde de cada página e tome decisões baseadas em dados reais.
            </p>
          </div>
        </div>

        <div className="premium-card p-6 bg-emerald-500/5 border-emerald-500/10 space-y-4">
          <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-400">
            <Plus className="w-5 h-5" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-white uppercase tracking-widest">Como Criar</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Clique no botão <span className="text-emerald-400 font-bold">"Nova Página"</span> acima ou use o card de adição rápida no final da lista. Dê um nome ao seu projeto e comece a editar.
            </p>
          </div>
        </div>

        <div className="premium-card p-6 bg-amber-500/5 border-amber-500/10 space-y-4">
          <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center text-amber-400">
            <Edit3 className="w-5 h-5" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-white uppercase tracking-widest">Como Gerenciar</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Use os ícones de ação em cada card para editar o conteúdo, alternar entre Rascunho/Publicado, visualizar estatísticas detalhadas ou excluir páginas obsoletas.
            </p>
          </div>
        </div>
      </div>

      {pages.length === 0 ? (
        <div className="premium-card p-20 flex flex-col items-center justify-center text-center space-y-6">
          <div className="w-20 h-20 bg-indigo-500/10 rounded-3xl flex items-center justify-center text-indigo-500">
            <Layers className="w-10 h-10" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-bold text-white">Nenhuma página criada</h3>
            <p className="text-slate-500 max-w-xs mx-auto">Comece criando sua primeira landing page para otimizar suas conversões.</p>
          </div>
          <form onSubmit={handleCreatePage} className="flex gap-3 w-full max-w-sm">
            <input 
              type="text" 
              placeholder="Nome da página..."
              className="flex-1 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none"
              value={newPageName}
              onChange={(e) => setNewPageName(e.target.value)}
              required
            />
            <button 
              type="submit"
              disabled={isCreating}
              className="px-6 py-2 bg-white text-black text-sm font-bold rounded-xl hover:bg-slate-200 transition-all disabled:opacity-50"
            >
              {isCreating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Criar'}
            </button>
          </form>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPages.map(page => (
            <div key={page.id} className="premium-card group hover:border-indigo-500/30 transition-all">
              <div className="p-6 space-y-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <h3 className="text-lg font-bold text-white group-hover:text-indigo-400 transition-colors">{page.name}</h3>
                    <div className="flex items-center gap-2 text-[12px] text-slate-400 font-bold uppercase tracking-widest">
                      <Globe className="w-3.5 h-3.5" />
                      {page.url?.replace('https://', '') || 'URL em geração...'}
                    </div>
                    
                    {/* Performance Metrics Chips */}
                    <div className="flex flex-wrap gap-2 mt-4">
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-white/[0.03] border border-white/5 rounded-xl group/metric relative">
                        <Eye className="w-3.5 h-3.5 text-indigo-400" />
                        <span className="text-[12px] font-bold text-slate-300 uppercase tracking-widest">{page.traffic.toLocaleString()}</span>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-48 p-3 bg-slate-900 border border-white/10 rounded-xl text-[12px] text-slate-400 opacity-0 invisible group-hover/metric:opacity-100 group-hover/metric:visible transition-all z-50 shadow-2xl font-normal normal-case tracking-normal backdrop-blur-xl">
                          Total de visitantes únicos que acessaram esta página.
                        </div>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-white/[0.03] border border-white/5 rounded-xl group/metric relative">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                        <span className="text-[12px] font-bold text-slate-300 uppercase tracking-widest">{page.conversions.toLocaleString()}</span>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-48 p-3 bg-slate-900 border border-white/10 rounded-xl text-[12px] text-slate-400 opacity-0 invisible group-hover/metric:opacity-100 group-hover/metric:visible transition-all z-50 shadow-2xl font-normal normal-case tracking-normal backdrop-blur-xl">
                          Total de leads ou vendas geradas através desta página.
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/[0.03] border border-white/5 rounded-lg group/metric relative">
                        <TrendingUp className="w-3 h-3 text-amber-500" />
                        <span className="text-[12px] font-bold text-slate-300 uppercase tracking-widest">
                          {page.traffic > 0 ? ((page.conversions / page.traffic) * 100).toFixed(1) : '0'}%
                        </span>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-3 bg-slate-900 border border-white/10 rounded-xl text-[12px] text-slate-400 opacity-0 invisible group-hover/metric:opacity-100 group-hover/metric:visible transition-all z-50 shadow-2xl font-normal normal-case tracking-normal backdrop-blur-xl">
                          Taxa de conversão: porcentagem de visitantes que se tornaram leads.
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded-md text-[12px] font-bold uppercase tracking-widest border ${
                      page.status === 'published' 
                        ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' 
                        : 'bg-amber-500/10 text-amber-500 border-amber-500/20'
                    }`}>
                      {page.status === 'published' ? 'Publicado' : page.status === 'draft' ? 'Rascunho' : 'Arquivado'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 py-5 border-y border-white/5">
                  <div className="space-y-2">
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Tráfego</p>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-white">{page.traffic.toLocaleString()}</span>
                      <BarChart2 className="w-5 h-5 text-slate-600" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Conversões</p>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-white">{page.conversions.toLocaleString()}</span>
                      <CheckCircle2 className="w-5 h-5 text-emerald-500/50" />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-white/5">
                  <div className="flex items-center gap-1 sm:gap-2">
                    <a 
                      href={page.url || `/p/${page.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 lg:w-11 lg:h-11 flex items-center justify-center text-slate-500 hover:text-indigo-400 transition-colors bg-white/[0.03] lg:bg-transparent rounded-xl"
                      title="Ver Página ao Vivo"
                    >
                      <ExternalLink className="w-6 h-6 lg:w-5 lg:h-5" />
                    </a>
                    <button 
                      onClick={() => toggleStatus(page)}
                      className="w-12 h-12 lg:w-11 lg:h-11 flex items-center justify-center text-slate-500 hover:text-white transition-colors bg-white/[0.03] lg:bg-transparent rounded-xl"
                      title={page.status === 'published' ? 'Despublicar' : 'Publicar'}
                    >
                      {page.status === 'published' ? <XCircle className="w-6 h-6 lg:w-5 lg:h-5" /> : <CheckCircle2 className="w-6 h-6 lg:w-5 lg:h-5" />}
                    </button>
                    <button 
                      onClick={() => {
                        setSelectedPage(page);
                        setIsAnalysisOpen(true);
                      }}
                      className="w-12 h-12 lg:w-11 lg:h-11 flex items-center justify-center text-indigo-400 hover:text-indigo-300 transition-colors bg-indigo-500/10 rounded-xl"
                      title="Análise de IA"
                    >
                      <Sparkles className="w-6 h-6 lg:w-5 lg:h-5" />
                    </button>
                    <button 
                      onClick={() => setDeleteModal({ isOpen: true, pageId: page.id })}
                      className="w-12 h-12 lg:w-11 lg:h-11 flex items-center justify-center text-slate-500 hover:text-rose-500 transition-colors bg-white/[0.03] lg:bg-transparent rounded-xl"
                      title="Excluir"
                    >
                      <Trash2 className="w-6 h-6 lg:w-5 lg:h-5" />
                    </button>
                  </div>
                  <button 
                    onClick={() => setEditingPageId(page.id)}
                    className="px-8 py-4 lg:px-6 lg:py-3 bg-indigo-500 lg:bg-white/[0.05] hover:bg-indigo-400 lg:hover:bg-white/[0.1] text-white text-[13px] lg:text-[11px] font-bold uppercase tracking-widest rounded-xl transition-all flex items-center gap-3 shadow-lg shadow-indigo-500/10 lg:shadow-none"
                  >
                    Editar <Edit3 className="w-5 h-5 lg:w-4 lg:h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {/* Quick Add Card */}
          <div className="border-2 border-dashed border-white/5 rounded-[32px] p-6 flex flex-col items-center justify-center text-center space-y-4 hover:border-indigo-500/30 transition-all group cursor-pointer" onClick={onCreate}>
            <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center text-slate-700 group-hover:text-indigo-500 transition-colors">
              <Plus className="w-6 h-6" />
            </div>
            <p className="text-sm font-bold text-slate-600 group-hover:text-slate-400 transition-colors">Adicionar Nova Página</p>
          </div>
        </div>
      )}

      {/* Create Modal Overlay */}
      {isCreating && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 lg:p-0 pt-10 lg:pt-20 overflow-y-auto">
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setIsCreating(false)}
          />
          <div className="relative max-w-md w-full premium-card p-10 space-y-8 animate-slide-up my-8">
            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-white">Nova Landing Page</h3>
              <p className="text-sm text-slate-500">Dê um nome para sua nova página de vendas.</p>
            </div>
            <form onSubmit={handleCreatePage} className="space-y-6">
              <div className="space-y-2">
                <label className="micro-label">Nome da Página</label>
                <input 
                  type="text" 
                  autoFocus
                  placeholder="Ex: Lançamento Black Friday"
                  className="premium-input"
                  value={newPageName}
                  onChange={(e) => setNewPageName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-3">
                <label className="micro-label">Escolha um Modelo (Template)</label>
                <div className="grid grid-cols-1 gap-3">
                  <button 
                    type="button"
                    onClick={() => setSelectedTemplate('vendas')}
                    className={`p-4 rounded-2xl border-2 text-left transition-all flex items-center gap-4 ${selectedTemplate === 'vendas' ? 'bg-indigo-500/10 border-indigo-500 shadow-lg shadow-indigo-500/10' : 'bg-white/[0.03] border-white/5 hover:border-white/10'}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${selectedTemplate === 'vendas' ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-500'}`}>
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white">Página de Vendas</p>
                      <p className="text-[10px] text-slate-500">Focada em conversão direta e persuasão.</p>
                    </div>
                  </button>

                  <button 
                    type="button"
                    onClick={() => setSelectedTemplate('leads')}
                    className={`p-4 rounded-2xl border-2 text-left transition-all flex items-center gap-4 ${selectedTemplate === 'leads' ? 'bg-indigo-500/10 border-indigo-500 shadow-lg shadow-indigo-500/10' : 'bg-white/[0.03] border-white/5 hover:border-white/10'}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${selectedTemplate === 'leads' ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-500'}`}>
                      <Search className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white">Captura de Leads</p>
                      <p className="text-[10px] text-slate-500">Ideal para ebooks, webinars e listas.</p>
                    </div>
                  </button>

                  <button 
                    type="button"
                    onClick={() => setSelectedTemplate('obrigado')}
                    className={`p-4 rounded-2xl border-2 text-left transition-all flex items-center gap-4 ${selectedTemplate === 'obrigado' ? 'bg-indigo-500/10 border-indigo-500 shadow-lg shadow-indigo-500/10' : 'bg-white/[0.03] border-white/5 hover:border-white/10'}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${selectedTemplate === 'obrigado' ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-500'}`}>
                      <CheckCircle2 className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white">Página de Obrigado</p>
                      <p className="text-[10px] text-slate-500">Confirmação de inscrição e próximos passos.</p>
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button 
                  type="button"
                  onClick={() => setIsCreating(false)}
                  className="flex-1 py-4 bg-white/5 text-white font-bold rounded-2xl hover:bg-white/10 transition-all uppercase tracking-widest text-[10px]"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  disabled={isCreating && newPageName === ''}
                  className="flex-1 py-4 bg-indigo-500 text-white font-bold rounded-2xl shadow-xl shadow-indigo-500/20 hover:bg-indigo-400 transition-all uppercase tracking-widest text-[10px]"
                >
                  Criar Página
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal 
        isOpen={deleteModal.isOpen}
        title="Excluir Página"
        message="Tem certeza que deseja excluir esta página? Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        type="danger"
        onConfirm={handleDeletePage}
        onCancel={() => setDeleteModal({ isOpen: false, pageId: null })}
      />

      <AIAnalysisModal 
        isOpen={isAnalysisOpen}
        onClose={() => setIsAnalysisOpen(false)}
        page={selectedPage}
      />
    </div>
  );
};
